import { RefreshCwIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { ReturnReceiptStatus, ReturnStatus, type ReturnDTO } from '@invenco/supply-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import { HeaderActions } from '@/components/header';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';

type Props = {
  returnDto?: ReturnDTO;
  isLoading?: boolean;
  onRefresh: () => Promise<unknown>;
};

const CANCELLABLE_STATUSES: ReturnStatus[] = [ReturnStatus.PENDING, ReturnStatus.OPEN];

export function ReturnActions({ returnDto, isLoading, onRefresh }: Props) {
  const { supplyGateway } = useGateways();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { mutate: cancelReturn } = useGatewayMutation({
    mutationFn: () =>
      returnDto
        ? supplyGateway.cancelReturn(returnDto.id)
        : Promise.reject(new Error('No return to cancel')),
    linkedQuery: ['returns', returnDto?.id],
    successMessage: 'Return cancelled',
  });

  // ReturnDTO has no `allowableActions` (unlike AsnDTO), so cancellability is
  // inferred from status here. The API remains authoritative and may still
  // reject (HTTP 409), which surfaces via the mutation's error toast.
  const canCancel =
    !isLoading &&
    !!returnDto &&
    CANCELLABLE_STATUSES.includes(returnDto.status) &&
    returnDto.receiptStatus !== ReturnReceiptStatus.PROCESSING;

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <HeaderActions>
      {canCancel ? (
        <AlertDialogWithAction onAction={cancelReturn}>
          <AlertDialogWithActionTrigger asChild>
            <Button variant="outline">
              <XIcon />
              Cancel
            </Button>
          </AlertDialogWithActionTrigger>
          <AlertDialogWithActionContent>
            <AlertDialogWithActionHeader>
              <AlertDialogWithActionTitle>
                Do you want to proceed with the return cancellation?
              </AlertDialogWithActionTitle>
              <AlertDialogWithActionDescription>
                This cancels the return and cannot be undone.
              </AlertDialogWithActionDescription>
            </AlertDialogWithActionHeader>
            <AlertDialogWithActionFooter>
              <AlertDialogWithActionCancel>No</AlertDialogWithActionCancel>
              <AlertDialogWithActionSubmit>Yes</AlertDialogWithActionSubmit>
            </AlertDialogWithActionFooter>
          </AlertDialogWithActionContent>
        </AlertDialogWithAction>
      ) : null}
      <Button
        size="icon"
        variant="outline"
        aria-label="Refresh"
        loading={isRefreshing}
        disabled={isLoading}
        onClick={() => void refresh()}
      >
        <RefreshCwIcon />
      </Button>
    </HeaderActions>
  );
}
